/**
 * Source accounting facts: what TallyPrime reported, normalised — and nothing
 * Timely decided (docs/tally/16-RECEIVABLES-INTELLIGENCE.md §1).
 *
 * The receivables pipeline has three layers that must never blur:
 *
 *   1. **Source facts** (this file) — ledger identity and group, the closing
 *      balance, every open bill with its reference, bill date, due date and
 *      pending amount, the money in the balance that sits in no bill, and,
 *      when the connector read them, the vouchers with their bill
 *      allocations. Nothing here depends on a company's preferences.
 *   2. **Timely's interpretation** (`receivablesPosition.ts`, `ageingEngine.ts`)
 *      — ageing basis and buckets, grace, credits shown apart. Computed from
 *      the facts; never written back into them.
 *   3. **The collection workflow** — follow-ups, promises, cheques, ranks,
 *      Timely's own operational allocations. Never derived from settings and
 *      never able to change a fact.
 *
 * Money is held in paise internally wherever it is added up (`toPaise`), so
 * every "these parts add up to the whole" check is exact rather than "close".
 *
 * Sign convention, as everywhere in the extract: **positive = Dr** (the
 * customer owes the company), negative = Cr (the company holds the
 * customer's money).
 */
import type { TallyExtract } from './tallySource.js';
import { tallySourceKey } from './tallyKey.js';

/* ------------------------------- money --------------------------------- */

/** An amount in paise. Sums of these are exact. */
export const toPaise = (rupees: number): number => Math.round((Number(rupees) || 0) * 100);
export const fromPaise = (paise: number): number => Math.round(paise) / 100;
/** Adds rupee amounts exactly (through paise). */
export const sumMoney = (values: readonly number[]): number => fromPaise(values.reduce((s, v) => s + toPaise(v), 0));
export const isZeroMoney = (value: number): boolean => toPaise(value) === 0;

/* ------------------------------- dates --------------------------------- */

const DAY_MS = 86_400_000;
/** Whole days from one YYYY-MM-DD to another, by the calendar (UTC; leap years included). */
export const daysFrom = (from: string, to: string): number =>
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
export const addDays = (isoDate: string, days: number): string =>
    new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

/* ------------------------------- facts --------------------------------- */

/** One open bill, exactly as TallyPrime's Bills Receivable / Bills Payable report states it. */
export interface SourceBillFact {
    /**
     * The bill's identity within its account. TallyPrime knows a bill by its
     * name (the reference a receipt is entered "Agst Ref" to), so the key is
     * the reference; only when a reference appears twice in one account is the
     * bill date added, and then a counter (`duplicateRef` says so).
     */
    key: string;
    ref: string;
    /** YYYY-MM-DD, as TallyPrime reports it. Never changed by any setting. */
    billDate: string;
    /** YYYY-MM-DD, as TallyPrime reports it; absent when TallyPrime gave none. Never invented. */
    dueDate?: string;
    /** Signed, positive = Dr: what is still pending on the bill. */
    pending: number;
    /** TallyPrime's own overdue days (BILLOVERDUE), kept only to cross-check ours. */
    tallyOverdueDays?: number;
    /** The reference occurs more than once in this account (a data-quality finding). */
    duplicateRef?: boolean;
}

/**
 * Why some of an account's balance is in no bill.
 *
 *   not_bill_wise — the ledger does not maintain balances bill-by-bill in
 *                   TallyPrime, so it has no bills at all;
 *   on_account    — a bill-wise ledger whose balance differs from its open
 *                   bills: money received (or charged) "On Account", or an
 *                   opening balance entered without bills.
 */
export type UnbilledReason = 'not_bill_wise' | 'on_account';

export interface SourceAccountFact {
    /** `tally:<ledger guid>` — the account's identity in the book. */
    sourceKey: string;
    ledgerGuid: string;
    name: string;
    /** The ledger's group in TallyPrime (Sundry Debtors, or a group under it). */
    group: string;
    billWise: boolean;
    /** The ledger's closing balance, signed (positive = Dr). */
    closing: number;
    bills: SourceBillFact[];
    /**
     * The part of the closing balance that is in no open bill
     * (closing − Σ pending), signed. Zero for a tidy bill-wise ledger.
     */
    unbilled: number;
    unbilledReason: UnbilledReason | null;
    creditPeriodDays?: number;
}

/** How a voucher line is allocated against bills, in TallyPrime's own four words. */
export type BillAllocationKind = 'agst_ref' | 'new_ref' | 'advance' | 'on_account';

export interface VoucherBillAllocation {
    kind: BillAllocationKind;
    /** The bill's name. Empty for On Account. */
    ref: string;
    /** Signed, positive = Dr to the customer. A receipt's allocations are negative. */
    amount: number;
}

/** A voucher that touched a customer account, as the connector read it (when it read vouchers at all). */
export interface SourceVoucherFact {
    /** TallyPrime's GUID for the voucher: stable when the voucher is altered. */
    guid: string;
    masterId?: number;
    /** Changes every time the voucher is altered in TallyPrime. */
    alterId?: number;
    /** YYYY-MM-DD */
    date: string;
    /** The voucher type's name as the company calls it (Receipt, Bank Receipt, Credit Note, Journal…). */
    type: string;
    number?: string;
    /** The customer ledger this voucher's party line is for. */
    sourceKey: string;
    /** The party line's amount, signed (positive = Dr to the customer; a receipt is negative). */
    partyAmount: number;
    /**
     * The party line's bill allocations. `allocationsRead: false` means the
     * connector could not read them for this voucher — not that there are none.
     */
    allocations: VoucherBillAllocation[];
    allocationsRead: boolean;
    instrument?: { number?: string; date?: string; bank?: string; kind?: string };
    cancelled: boolean;
    optional: boolean;
    postDated: boolean;
}

export interface SourceFacts {
    source: { kind: 'tally'; companyId: string; companyName: string };
    /** YYYY-MM-DD: the date the outstanding is stated at. */
    asOf: string;
    capturedAt: string;
    accounts: SourceAccountFact[];
    /** Bills TallyPrime listed that could not be tied to exactly one customer ledger. Never aged, never guessed. */
    unmatchedBills: { party: string; ref: string; billDate: string; pending: number }[];
    /** Present only when the connector read vouchers; their absence is not "no vouchers". */
    vouchers?: SourceVoucherFact[];
    voucherWindow?: { from: string; to: string };
}

/* ----------------------------- bill identity ----------------------------- */

const normRef = (ref: string): string => ref.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Keys for one account's bills: the reference, or — only where a reference
 * repeats — the reference and the bill date, then a counter. Deterministic for
 * a given order of bills, and flags the repeats.
 */
export function keyBills(bills: readonly Omit<SourceBillFact, 'key' | 'duplicateRef'>[]): SourceBillFact[] {
    const byRef = new Map<string, number>();
    for (const b of bills) byRef.set(normRef(b.ref), (byRef.get(normRef(b.ref)) ?? 0) + 1);
    const used = new Map<string, number>();
    return [...bills]
        .sort((a, b) => a.billDate.localeCompare(b.billDate) || normRef(a.ref).localeCompare(normRef(b.ref)) || a.pending - b.pending)
        .map(b => {
            const ref = normRef(b.ref);
            const repeated = (byRef.get(ref) ?? 0) > 1;
            let key = repeated ? `ref:${ref}@${b.billDate}` : `ref:${ref}`;
            const n = (used.get(key) ?? 0) + 1;
            used.set(key, n);
            if (n > 1) key = `${key}#${n}`;
            return { ...b, key, ...(repeated ? { duplicateRef: true } : {}) };
        });
}

/* ------------------------- from the Tally extract ------------------------ */

/**
 * The facts in a validated TallyPrime extract. Bills are tied to a ledger by
 * the GUID the connector resolved, else by a name that identifies exactly one
 * ledger — the same rule `normalizeTallyExtract` uses, so the two cannot
 * disagree about which bills belong where.
 */
export function factsFromTallyExtract(extract: TallyExtract): SourceFacts {
    const byGuid = new Map(extract.ledgers.map(l => [l.guid.toLowerCase(), l]));
    const byName = new Map<string, typeof extract.ledgers>();
    for (const l of extract.ledgers) {
        const k = l.name.trim().toLowerCase();
        byName.set(k, [...(byName.get(k) ?? []), l]);
    }
    const billsOf = new Map<string, TallyExtract['bills']>();
    const unmatchedBills: SourceFacts['unmatchedBills'] = [];
    for (const bill of extract.bills) {
        const byId = bill.ledgerGuid ? byGuid.get(bill.ledgerGuid.toLowerCase()) : undefined;
        const named = byName.get(bill.party.trim().toLowerCase());
        const ledger = byId ?? (named && named.length === 1 ? named[0] : undefined);
        if (!ledger) {
            unmatchedBills.push({ party: bill.party, ref: bill.ref, billDate: bill.billDate, pending: bill.pending });
            continue;
        }
        const k = ledger.guid.toLowerCase();
        billsOf.set(k, [...(billsOf.get(k) ?? []), bill]);
    }

    const accounts: SourceAccountFact[] = extract.ledgers.map(ledger => {
        const bills = keyBills((billsOf.get(ledger.guid.toLowerCase()) ?? []).map(b => ({
            ref: b.ref, billDate: b.billDate,
            ...(b.dueDate ? { dueDate: b.dueDate } : {}),
            pending: b.pending,
            ...(b.overdueDays !== undefined ? { tallyOverdueDays: b.overdueDays } : {}),
        })));
        const unbilled = fromPaise(toPaise(ledger.closingBalance) - bills.reduce((s, b) => s + toPaise(b.pending), 0));
        return {
            sourceKey: tallySourceKey(ledger.guid),
            ledgerGuid: ledger.guid,
            name: ledger.name.trim(),
            group: ledger.parent,
            billWise: ledger.billWise,
            closing: ledger.closingBalance,
            bills,
            unbilled,
            unbilledReason: isZeroMoney(unbilled) ? null : ledger.billWise ? 'on_account' : 'not_bill_wise',
            ...(ledger.creditPeriodDays !== undefined ? { creditPeriodDays: ledger.creditPeriodDays } : {}),
        };
    });

    return {
        source: { kind: 'tally', companyId: extract.company.guid, companyName: extract.company.name },
        asOf: extract.asOf,
        capturedAt: extract.capturedAt,
        accounts,
        unmatchedBills,
        ...(extract.vouchers ? { vouchers: extract.vouchers.map(v => voucherFact(v, byGuid, byName)).filter((v): v is SourceVoucherFact => v !== null) } : {}),
        ...(extract.voucherWindow ? { voucherWindow: extract.voucherWindow } : {}),
    };
}

function voucherFact(
    v: NonNullable<TallyExtract['vouchers']>[number],
    byGuid: Map<string, TallyExtract['ledgers'][number]>,
    byName: Map<string, TallyExtract['ledgers']>,
): SourceVoucherFact | null {
    const byId = v.partyLedgerGuid ? byGuid.get(v.partyLedgerGuid.toLowerCase()) : undefined;
    const named = byName.get(v.party.trim().toLowerCase());
    const ledger = byId ?? (named && named.length === 1 ? named[0] : undefined);
    if (!ledger) return null;
    return {
        guid: v.guid,
        ...(v.masterId !== undefined ? { masterId: v.masterId } : {}),
        ...(v.alterId !== undefined ? { alterId: v.alterId } : {}),
        date: v.date,
        type: v.type,
        ...(v.number ? { number: v.number } : {}),
        sourceKey: tallySourceKey(ledger.guid),
        partyAmount: v.partyAmount,
        allocations: v.allocations.map(a => ({ kind: a.kind, ref: a.ref, amount: a.amount })),
        allocationsRead: v.allocationsRead,
        ...(v.instrument ? { instrument: { ...v.instrument } } : {}),
        cancelled: v.cancelled,
        optional: v.optional,
        postDated: v.postDated,
    };
}

/** The open bills of an account on the debit side (what the customer owes, bill by bill). */
export const debitBills = (a: Pick<SourceAccountFact, 'bills'>): SourceBillFact[] => a.bills.filter(b => toPaise(b.pending) > 0);
/** The open bills on the credit side: credit notes entered as new references, advances. */
export const creditBills = (a: Pick<SourceAccountFact, 'bills'>): SourceBillFact[] => a.bills.filter(b => toPaise(b.pending) < 0);
